/*
 * ModelViewer.js — Go AR · Effects
 *
 * Hỗ trợ 2 display mode:
 *
 *   'anchor' (mặc định):
 *     GLB gắn vào anchor.group → di chuyển theo bao bì
 *
 *   'screen':
 *     GLB gắn vào camera → luôn hiển thị tại vị trí cố định trên màn hình
 *     Cấu hình vị trí qua display.position [x, y, z] tính từ camera
 *       x: âm=trái, dương=phải  (range: -1 ~ 1)
 *       y: âm=xuống, dương=lên  (range: -1 ~ 1)
 *       z: khoảng cách khỏi camera, âm = ra trước mặt (vd: -1.5)
 *
 * Config products.js:
 *   display: {
 *     space:    'screen',
 *     position: [0, -0.3, -1.5],
 *   },
 *   animations: {
 *     open:  null,       // null = track đầu tiên
 *     idle:  null,
 *     entry: {
 *       from:     'bottom',
 *       duration: 0.8,
 *       easing:   'easeOut',
 *     }
 *   }
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const loader = new GLTFLoader();

const EASINGS = {
  linear:    t => t,
  easeOut:   t => 1 - Math.pow(1 - t, 3),
  easeInOut: t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2,
};

export class ModelViewer {
  constructor(anchorGroup, product, camera, hudScene, hudCamera) {
    this.group     = anchorGroup;
    this.product   = product;
    this.camera    = camera;
    this.hudScene  = hudScene;
    this.hudCamera = hudCamera;
    this.mixer     = null;
    this.model     = null;
    this._tween    = null;
    this._phase    = 'idle';
    this._wrapper  = new THREE.Group();
    this._wrapper.visible = false;

    this._space = product.display?.space ?? 'anchor';

    if (this._space === 'screen') {
      /* Gắn vào hudScene — render độc lập đè lên AR */
      this.hudScene.add(this._wrapper);
      console.log('[ModelViewer] screen-space: attached to hudScene');
    } else {
      this.group.add(this._wrapper);
    }
  }

  load() {
    return new Promise((resolve, reject) => {
      const { modelUrl, modelScale } = this.product;
      const display = this.product.display ?? {};

      loader.load(modelUrl, (gltf) => {
        this.model = gltf.scene;
        this.model.scale.setScalar(modelScale ?? 0.08);

        /* Auto-center GLB về origin */
        const autoCenter = this.product.autoCenter ?? true;
        if (autoCenter) {
          const box    = new THREE.Box3().setFromObject(this.model);
          const center = new THREE.Vector3();
          box.getCenter(center);
          this.model.position.sub(center);
        }

        /* Target position:
         *   screen mode → [x, y, z] tính từ camera
         *   anchor mode → [x, y, z] tính từ anchor */
        const pos = display.position ?? this.product.modelPosition ?? [0, 0, 0];
        this._targetPos = new THREE.Vector3(...pos);
        this._wrapper.position.copy(this._targetPos);

        this._wrapper.add(this.model);

        if (gltf.animations.length > 0) {
          this.mixer  = new THREE.AnimationMixer(this.model);
          this._clips = {};
          gltf.animations.forEach(c => { this._clips[c.name] = c; });
          console.log(`[ModelViewer][${this._space}] GLB tracks:`, Object.keys(this._clips));
        }

        resolve(this);
      }, undefined, reject);
    });
  }

  /* ── playOpen ── */
  playOpen(onComplete) {
    if (!this._wrapper) return;
    this._onPlayComplete = onComplete; // lưu lại để gọi sau entry + GLB

    console.log('[ModelViewer] playOpen, space:', this._space,
      'wrapper visible:', this._wrapper.visible,
      'camera children:', this.camera.children.length,
      'targetPos:', this._targetPos);

    const entry = this.product.animations?.entry;

    if (entry?.from) {
      this._startEntryTween(entry, () => {
        this._playGLBAnimation();
      });
    } else {
      this._wrapper.visible = true;
      this._playGLBAnimation();
      /* Không có entry → hiện card ngay */
      this._onPlayComplete?.();
      this._onPlayComplete = null;
    }
  }

  /* ── Entry tween ──────────────────────────────────────────────────────────
   * screen mode: tween trong camera local space — đơn giản hơn nhiều
   * anchor mode: tween trong world space rồi chuyển về local của anchor
   * ─────────────────────────────────────────────────────────────────────── */
  _startEntryTween(entry, onComplete) {
    const duration = entry.duration ?? 0.8;
    const easing   = EASINGS[entry.easing ?? 'easeOut'] ?? EASINGS.easeOut;
    const from     = entry.from;
    const PUSH     = 2.5;

    let startLocal;
    const targetLocal = this._targetPos.clone();

    if (this._space === 'screen') {
      /* Screen mode: tọa độ đã là camera local space
       * Chỉ cần offset thêm theo hướng */
      startLocal = targetLocal.clone();
      switch (from) {
        case 'bottom': startLocal.y -= PUSH; break;
        case 'top':    startLocal.y += PUSH; break;
        case 'left':   startLocal.x -= PUSH; break;
        case 'right':  startLocal.x += PUSH; break;
      }

    } else {
      /* Anchor mode: tính world position rồi unproject */
      const targetWorld = new THREE.Vector3();
      this._wrapper.getWorldPosition(targetWorld);
      const targetNDC = targetWorld.clone().project(this.camera);

      const startNDC = targetNDC.clone();
      switch (from) {
        case 'bottom': startNDC.y = -PUSH; break;
        case 'top':    startNDC.y =  PUSH; break;
        case 'left':   startNDC.x = -PUSH; break;
        case 'right':  startNDC.x =  PUSH; break;
      }

      const startWorld = new THREE.Vector3(startNDC.x, startNDC.y, targetNDC.z)
        .unproject(this.camera);

      const invMatrix = new THREE.Matrix4()
        .copy(this.group.matrixWorld)
        .invert();
      startLocal = startWorld.applyMatrix4(invMatrix);
    }

    this._wrapper.position.copy(startLocal);
    this._wrapper.visible = true;

    this._tween = {
      elapsed: 0,
      duration,
      easing,
      from: startLocal.clone(),
      to:   targetLocal.clone(),
      onComplete,
    };

    this._phase = 'entering';
  }

  _tickTween(dt) {
    if (!this._tween) return;
    const tw = this._tween;
    tw.elapsed += dt;
    const t = Math.min(tw.elapsed / tw.duration, 1);
    this._wrapper.position.lerpVectors(tw.from, tw.to, tw.easing(t));
    if (t >= 1) {
      this._wrapper.position.copy(tw.to);
      this._tween = null;
      tw.onComplete?.();
      /* Notify ARScreen → hiện ProductCard */
      this._onPlayComplete?.();
      this._onPlayComplete = null;
    }
  }

  /* ── GLB animation ── */
  _playGLBAnimation() {
    if (!this.mixer) { this._phase = 'done'; return; }

    const openClip = this._findClip(this.product.animations?.open);
    if (!openClip) { this._playIdle(); return; }

    this._phase = 'opening';
    const action = this.mixer.clipAction(openClip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.reset().play();

    const onFinish = (e) => {
      if (e.action !== action) return;
      this.mixer.removeEventListener('finished', onFinish);
      this._playIdle();
    };
    this.mixer.addEventListener('finished', onFinish);
  }

  _playIdle() {
    const { idle } = this.product.animations ?? {};
    const idleClip = this._findClip(idle);
    if (!idle || !idleClip) { this._phase = 'done'; return; }
    const openName = this._findClip(this.product.animations?.open)?.name;
    if (idleClip.name === openName) { this._phase = 'done'; return; }
    this._phase = 'looping';
    this.mixer.clipAction(idleClip).setLoop(THREE.LoopRepeat).reset().play();
  }

  _findClip(name) {
    if (!this._clips) return null;
    const clips = Object.values(this._clips);
    if (!clips.length) return null;
    if (name && this._clips[name]) return this._clips[name];
    if (name) console.warn(`[ModelViewer] Track "${name}" not found, using: "${clips[0].name}"`);
    return clips[0];
  }

  pause() {
    this._tween = null;
    this._phase = 'idle';
    if (this._wrapper) {
      this._wrapper.visible = false;
      this._wrapper.position.copy(this._targetPos ?? new THREE.Vector3());
    }
    if (this.mixer) {
      this.mixer.stopAllAction();
      if (this._clips) {
        Object.values(this._clips).forEach(clip => {
          this.mixer.existingAction(clip)?.reset();
        });
      }
      this.mixer.setTime(0);
    }
  }

  update(dt) {
    if (this._tween) this._tickTween(dt);
    if (this.mixer)  this.mixer.update(dt);
  }

  dispose() {
    this._tween = null;
    if (this._wrapper) {
      const parent = this._space === 'screen' ? this.hudScene : this.group;
      parent.remove(this._wrapper);
      this._wrapper.traverse(obj => {
        if (obj.isMesh) {
          obj.geometry?.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material?.dispose();
        }
      });
    }
    if (this.mixer) this.mixer.stopAllAction();
  }
}